import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Check } from "lucide-react";
import api from "../api";
import { useLang, fmtPrice } from "../i18n";

export default function AcquirePanel({ artwork, open, onClose }) {
  const { t, loc, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [state, setState] = useState("idle");

  if (!artwork) return null;
  const title = loc(artwork, "title");

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await api.post("/enquiries", {
        artwork_slug: artwork.slug,
        artwork_title: title,
        name: form.name,
        email: form.email,
        message: form.message,
      });
      setState("sent");
    } catch {
      setState("idle");
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => setState("idle"), 400);
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#C5C0B7] py-3 text-sm placeholder:text-[#8C8983] transition-colors";

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="acquire-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[70]"
            style={{ background: "rgba(18,18,18,0.55)" }}
          />
          <motion.aside
            data-testid="acquire-panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[80] w-full sm:w-[480px] overflow-y-auto"
            style={{ background: "#FAF9F6", borderLeft: "1px solid #E5E2DC" }}
          >
            <div className="p-8 sm:p-10">
              <div className="flex items-start justify-between mb-10">
                <div>
                  <p className="font-mono-meta text-[11px] uppercase tracking-[0.2em] text-[#C83E2B] mb-3">
                    {t("acquire.title")}
                  </p>
                  <h3 className="font-serif-display text-3xl leading-tight">{title}</h3>
                  <p className="font-mono-meta text-xs text-[#8C8983] mt-2">
                    {artwork.dimensions} · {artwork.year} · {fmtPrice(artwork.price_eur, lang)}
                  </p>
                </div>
                <button
                  data-testid="acquire-close-btn"
                  onClick={close}
                  className="p-2 hover:rotate-90 transition-transform duration-300"
                  aria-label={t("acquire.close")}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-6 mb-10 pb-10 border-b" style={{ borderColor: "#E5E2DC" }}>
                {[1, 2, 3].map((n) => (
                  <div key={n} className="flex gap-4">
                    <span className="font-serif-display italic text-2xl text-[#C83E2B] leading-none">{n}.</span>
                    <div>
                      <p className="text-sm font-medium mb-1">{t(`acquire.step${n}_t`)}</p>
                      <p className="text-sm text-[#5A5853] leading-relaxed">{t(`acquire.step${n}`)}</p>
                    </div>
                  </div>
                ))}
              </div>

              {state === "sent" ? (
                <div data-testid="acquire-success" className="space-y-5">
                  <div className="flex items-center gap-3 text-[#121212]">
                    <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "#C83E2B" }}>
                      <Check size={16} color="#FAF9F6" />
                    </span>
                    <p className="font-serif-display text-2xl">{t("acquire.sent_title")}</p>
                  </div>
                  <p className="text-sm text-[#5A5853] leading-relaxed">{t("acquire.sent")}</p>
                  <a
                    href={`mailto:max.dhumerelle@gmail.com?subject=${encodeURIComponent(`Acquisition — ${title}`)}`}
                    className="link-line text-sm text-[#C83E2B] w-fit inline-block"
                    data-testid="acquire-mailto-link"
                  >
                    max.dhumerelle@gmail.com
                  </a>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-6" data-testid="acquire-form">
                  <input
                    data-testid="acquire-name-input"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t("acquire.name")}
                    className={inputCls}
                  />
                  <input
                    data-testid="acquire-email-input"
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder={t("acquire.email")}
                    className={inputCls}
                  />
                  <textarea
                    data-testid="acquire-message-input"
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder={t("acquire.message")}
                    className={`${inputCls} resize-none`}
                  />
                  <button
                    data-testid="acquire-submit-btn"
                    type="submit"
                    disabled={state === "sending"}
                    className="w-full py-4 font-mono-meta text-xs uppercase tracking-[0.2em] text-[#FAF9F6] transition-opacity hover:opacity-85 disabled:opacity-50"
                    style={{ background: "#121212" }}
                  >
                    {state === "sending" ? t("acquire.sending") : t("acquire.send")}
                  </button>
                  <a
                    href={`mailto:max.dhumerelle@gmail.com?subject=${encodeURIComponent(`Acquisition — ${title}`)}&body=${encodeURIComponent(`Bonjour,\n\nJe suis intéressé·e par « ${title} » (${artwork.dimensions}, ${artwork.year}, ${fmtPrice(artwork.price_eur, lang)}).\n\n`)}`}
                    className="block text-center font-mono-meta text-[11px] uppercase tracking-[0.18em] text-[#8C8983] hover:text-[#121212] transition-colors"
                    data-testid="acquire-direct-mail-link"
                  >
                    {t("acquire.direct")} →
                  </a>
                </form>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
